<script setup lang="ts">
const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== 'dark');

interface OverlayCard {
  title: string;
  description: string;
}

interface OverlayPosition {
  top: string;
  left: string;
  titleWidth?: string;
  descWidth?: string;
}

const props = withDefaults(defineProps<{
  heading?: string;
  subcopy?: string;
  cards?: OverlayCard[];
  darkSvgSrc?: string;
  lightSvgSrc?: string;
  overlayPositions?: OverlayPosition[];
}>(), {
  heading: '',
  subcopy: '',
  cards: () => [],
  darkSvgSrc: '/images/why-are-we-different-dark.svg',
  lightSvgSrc: '/images/why-are-we-different-light.svg',
  overlayPositions: () => [
    { top: '58%', left: '0%', titleWidth: '249px', descWidth: '262px' },
    { top: '20%', left: '17%', titleWidth: '249px', descWidth: '240px' },
    { top: '58%', left: '36%', titleWidth: '249px', descWidth: '260px' },
    { top: '20%', left: '55%', titleWidth: '249px', descWidth: '247px' },
    { top: '58%', left: '71%', titleWidth: '320px', descWidth: '288px' },
  ],
});

const defaultPosition: OverlayPosition = { top: '50%', left: '50%', titleWidth: '249px', descWidth: '240px' };
const getPosition = (index: number): OverlayPosition => props.overlayPositions[index] ?? defaultPosition;

const bgImageSrc = computed(() => isLight.value ? props.lightSvgSrc : props.darkSvgSrc);
</script>

<template>
  <section class="relative z-10 mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" :aria-label="heading">
    <!-- Heading -->
    <h2
      class="text-center text-[28px] lg:text-[48px] font-semibold leading-tight px-4 font-avant-demi text-transparent bg-clip-text bg-gradient-to-r from-[#12DBFF] to-[#BA55FE]"
    >
      {{ heading }}
    </h2>

    <!-- Subcopy -->
    <p
      class="text-center max-w-[720px] mx-auto mt-4 text-[16px] lg:text-[18px] font-normal tracking-[0.36px] lg:opacity-70 font-avant"
      :class="isLight ? 'text-black' : 'text-white'"
    >
      {{ subcopy }}
    </p>

    <!-- ==================== DESKTOP SVG ==================== -->
    <div class="relative w-full max-w-[973px] mx-auto mt-10 hidden lg:block">
      <!-- Dark/Light SVG graphic -->
      <img
        :src="bgImageSrc"
        :alt="heading"
        loading="lazy"
        class="w-full h-auto block"
      />

      <!-- Overlay Divs -->
      <div
        v-for="(card, index) in cards"
        :key="card.title"
        class="absolute border-none bg-none p-0"
        :style="{
          top: getPosition(index).top,
          left: getPosition(index).left,
        }"
      >
        <h3
          class="m-0 font-avant font-semibold"
          :style="{
            width: getPosition(index).titleWidth,
            fontSize: 'clamp(16px, 2.5vw, 20px)',
            color: isLight ? '#613FAA' : '#A561FF',
          }"
        >
          {{ card.title }}
        </h3>
        <p
          class="text-left mt-[8px] font-avant font-normal tracking-[0.3px]"
          :style="{
            width: getPosition(index).descWidth,
            fontSize: 'clamp(13px, 2vw, 15px)',
            color: isLight ? '#000000' : '#D9D9D9',
          }"
        >
          {{ card.description }}
        </p>
      </div>
    </div>

    <!-- ==================== MOBILE CARDS ==================== -->
    <div class="flex flex-col items-center gap-6 mt-8 lg:hidden w-full max-w-[520px] mx-auto px-4">
      <article
        v-for="(item, index) in cards"
        :key="item.title"
        class="mobile-overlay-card w-full rounded-xl text-left font-avant"
        :class="isLight ? 'bg-white border border-[rgba(34,13,66,0.08)]' : 'bg-[linear-gradient(93deg,#15052E_33.61%,#2A0B55_96.74%)] border border-[#3D1B60]'"
      >
        <!-- Number badge -->
        <div
          class="mobile-number-badge flex-shrink-0"
          :class="isLight ? 'light-badge' : 'dark-badge'"
        >
          {{ index + 1 }}
        </div>
        <!-- Context -->
        <div class="mobile-card-content">
          <h3
            class="m-0 font-avant font-semibold"
            :style="{ fontSize: 'clamp(18px, 4vw, 18px)' }"
            :class="isLight ? 'text-[#613FAA]' : 'text-[#A561FF]'"
          >
            {{ item.title }}
          </h3>
          <p
            class="mt-2 font-avant text-[15px] font-normal"
            :class="isLight ? 'text-black' : 'text-[#D9D9D9]'"
          >
            {{ item.description }}
          </p>
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped>
.mobile-overlay-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
}

.mobile-card-content {
  flex: 1;
  min-width: 0;
}

.mobile-number-badge {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  font-family: 'Avant Garde', 'Avantgarde', sans-serif;
  color: #ffffff;
}

.light-badge {
  background: linear-gradient(135deg, #613faa 0%, #a561ff 100%);
  box-shadow: 0 0 12px rgba(165, 97, 255, 0.35);
}

.dark-badge {
  background: linear-gradient(135deg, #7c3aed 0%, #a561ff 100%);
  box-shadow: 0 0 16px rgba(165, 97, 255, 0.45), 0 0 32px rgba(124, 58, 237, 0.2);
}
</style>
